import { translationKey } from '../../i18n';
import { localize } from '../../i18n';
import SingleLineText from '../../components/ui/SingleLineText';
import React, { useEffect, useRef } from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import medicationEditChipSx from './medicationEditChipSx';

const MEDICATION_CATEGORY_LABELS = {
    PRESCRIPTION: translationKey('처방약'),
    OTC: translationKey('일반약'),
    SUPPLEMENT: translationKey('영양제'),
    HERBAL: translationKey('한약'),
    OTHER: translationKey('기타'),
};

export const MEDICATION_CATEGORY_OPTIONS = Object.entries(MEDICATION_CATEGORY_LABELS)
    .map(([value, label]) => ({ value, label }));

export const MEDICATION_INTAKE_FORM_OPTIONS = [
    { value: 'ORAL', label: translationKey('먹는 약'), methodLabel: translationKey('복용법'), doseLabel: translationKey('1회 복용량'), unit: translationKey('정') },
    { value: 'TOPICAL', label: translationKey('바르는 약'), methodLabel: translationKey('사용 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('회') },
    { value: 'PATCH', label: translationKey('붙이는 약'), methodLabel: translationKey('교체 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('매') },
    { value: 'SPRAY', label: translationKey('뿌리는 약'), methodLabel: translationKey('사용 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('회') },
    { value: 'INSERT', label: translationKey('넣는 약'), methodLabel: translationKey('사용 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('개') },
    { value: 'OTHER', label: translationKey('기타'), methodLabel: translationKey('사용 방법'), doseLabel: translationKey('1회 사용량'), unit: translationKey('회') },
];

export const MEDICATION_DOSAGE_METHOD_OPTIONS = [
    { value: 'AFTER_MEAL', label: translationKey('식후') },
    { value: 'BEFORE_MEAL', label: translationKey('식전') },
    { value: 'BEFORE_SLEEP', label: translationKey('취침 전') },
    { value: 'ANYTIME', label: translationKey('상관없음') },
];

export function medicationIntakeMeta(value) {
    return MEDICATION_INTAKE_FORM_OPTIONS.find(option => option.value === value)
        || MEDICATION_INTAKE_FORM_OPTIONS[0];
}

export function MedicationOptionEditor({ title, options, value, onChange }) {
    return (
        <Box>
            {title && (
                <SingleLineText sx={{ fontSize: '0.88rem', fontWeight: 900, color: '#64748b', mb: 0.85 }}>
                    {localize(title)}
                </SingleLineText>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {options.map(option => (
                    <Chip
                        key={option.value}
                        label={localize(option.label)}
                        onClick={() => onChange(option.value)}
                        sx={medicationEditChipSx(value === option.value)}
                    />
                ))}
            </Box>
        </Box>
    );
}

export function MedicationDosagePerTimeEditor({ label, unit = translationKey('정'), value, onChange }) {
    const inputRef = useRef(null);
    const editedRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            const input = inputRef.current;
            if (!input || editedRef.current) return;
            input.focus();
            input.select?.();
        }, 120);
        return () => clearTimeout(timer);
    }, []);

    const handleChange = (event) => {
        editedRef.current = true;
        const rawValue = event.target.value;
        if (rawValue === '') {
            onChange('');
            return;
        }
        if (/^\d*(?:\.\d*)?$/.test(rawValue)) onChange(rawValue);
    };

    return (
        <TextField
            label={localize(label)}
            value={value ?? 1}
            onChange={handleChange}
            autoFocus
            inputRef={inputRef}
            fullWidth
            inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9.]*',
                'data-keyboard-lift-action': 'true',
            }}
            InputProps={{
                endAdornment: (
                    <Typography sx={{ color: '#64748b', fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {localize(unit)}
                    </Typography>
                ),
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    minHeight: 52,
                    borderRadius: 3,
                    bgcolor: '#fff',
                    fontWeight: 800,
                    fontSize: '1rem',
                },
                '& .MuiInputBase-input': {
                    py: 1.35,
                },
            }}
        />
    );
}

export function MedicationMemoEditor({ value, onChange }) {
    const inputRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            const input = inputRef.current;
            if (!input) return;
            input.focus();
            const length = input.value?.length || 0;
            input.setSelectionRange?.(length, length);
        }, 120);
        return () => clearTimeout(timer);
    }, []);

    return (
        <TextField
            value={value || ''}
            onChange={(event) => onChange(event.target.value)}
            autoFocus
            inputRef={inputRef}
            fullWidth
            multiline
            minRows={3}
            placeholder={localize("필요하면 메모를 남겨주세요")}
            inputProps={{
                maxLength: 1000,
                'data-keyboard-lift-action': 'true',
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: '#f9fafb',
                    fontWeight: 700,
                },
                '& .MuiInputBase-input': {
                    lineHeight: 1.55,
                },
            }}
        />
    );
}
